// src/models/BusinessProfile.js - Business Profile Model with Validation
import { ValidationService } from '../services/ValidationService';

export class BusinessProfile {
  constructor(data = {}) {
    // Basic Business Information
    this.id = data.id || null; // Phone number used as ID
    this.businessName = data.businessName || '';
    this.businessType = data.businessType || '';
    this.formattedAddress = data.formattedAddress || '';
    this.phoneNumber = data.phoneNumber || '';
    this.whatsappNumber = data.whatsappNumber || '';
    this.timezone = data.timezone || 'America/Toronto';
    
    // Operational Details
    this.numberOfChairs = data.numberOfChairs || '1';
    this.workingHours = data.workingHours || this.getDefaultWorkingHours();
    
    // Service Configuration
    this.appointmentDurationMinutes = data.appointmentDurationMinutes || {};
    
    // Subscription & Billing
    this.tier = data.tier || 'basic'; // basic, premium, business, elite
    this.billingCycle = data.billingCycle || 'monthly';
    
    // Owner Information
    this.ownerName = data.ownerName || '';
    this.ownerEmail = data.ownerEmail || '';
    
    // Settings (New in Phase 2)
    this.settings = {
      notifications: {
        emailEnabled: true,
        smsEnabled: false,
        bookingConfirmations: true,
        reminders: true,
        ...data.settings?.notifications
      },
      aiConfig: {
        greetingMessage: `Hello! Welcome to ${this.businessName}. I'm here to help you book an appointment.`,
        businessDescription: '',
        specialInstructions: '',
        ...data.settings?.aiConfig
      },
      integrations: {
        googleCalendarEnabled: false,
        stripeEnabled: false,
        whatsappBusinessVerified: false,
        ...data.settings?.integrations
      },
      ...data.settings
    };

    // Metadata
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.status = data.status || 'active'; // active, suspended, pending
  }

  // Default Working Hours
  getDefaultWorkingHours() {
    return {
      mon: '09:00-17:00',
      tue: '09:00-17:00', 
      wed: '09:00-17:00',
      thu: '09:00-17:00',
      fri: '09:00-17:00',
      sat: '09:00-17:00',
      sun: 'closed'
    };
  }

  // Validation Methods
  validate() {
    const errors = [];

    // Required fields validation
    if (!this.businessName || this.businessName.trim().length < 2) {
      errors.push('Business name must be at least 2 characters');
    }

    if (!this.businessType) {
      errors.push('Business type is required');
    }

    if (!this.formattedAddress || this.formattedAddress.trim().length < 5) {
      errors.push('Valid business address is required');
    }

    if (!ValidationService.isValidPhoneNumber(this.phoneNumber)) {
      errors.push('Valid phone number is required');
    }

    if (!ValidationService.isValidEmail(this.ownerEmail)) {
      errors.push('Valid owner email is required');
    }

    if (!this.ownerName || this.ownerName.trim().length < 2) {
      errors.push('Owner name must be at least 2 characters');
    }

    // Business type validation
    const validBusinessTypes = ['barbershop', 'hair_salon', 'nail_salon', 'spa', 'beauty_studio', 'other'];
    if (!validBusinessTypes.includes(this.businessType)) {
      errors.push('Invalid business type');
    }

    // Tier validation
    const validTiers = ['basic', 'premium', 'business', 'elite'];
    if (!validTiers.includes(this.tier)) {
      errors.push('Invalid subscription tier');
    }

    // Working hours validation
    const workingHoursValidation = this.validateWorkingHours();
    if (!workingHoursValidation.isValid) {
      errors.push(...workingHoursValidation.errors);
    }

    // Number of chairs validation
    const chairs = parseInt(this.numberOfChairs);
    if (isNaN(chairs) || chairs < 1 || chairs > 50) {
      errors.push('Number of chairs must be between 1 and 50');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateWorkingHours() {
    const errors = [];
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    days.forEach(day => {
      const hours = this.workingHours[day];
      
      if (!hours) {
        errors.push(`Working hours for ${day} is required`);
        return;
      }

      if (hours === 'closed') {
        return; // Valid closed day
      }

      // Validate time format (HH:MM-HH:MM)
      const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]-([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(hours)) {
        errors.push(`Invalid time format for ${day}. Use HH:MM-HH:MM or 'closed'`);
        return;
      }

      // Validate start time is before end time
      const [startTime, endTime] = hours.split('-');
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (startMinutes >= endMinutes) {
        errors.push(`Start time must be before end time for ${day}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Business Logic Methods
  isOpen(dayOfWeek, currentTime) {
    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const day = dayNames[dayOfWeek];
    const hours = this.workingHours[day];
    
    if (hours === 'closed') {
      return false;
    }

    const [startTime, endTime] = hours.split('-');
    return currentTime >= startTime && currentTime <= endTime;
  }

  getOpenDays() {
    return Object.entries(this.workingHours)
      .filter(([day, hours]) => hours !== 'closed')
      .map(([day, hours]) => ({ day, hours }));
  }

  hasService(serviceKey) {
    return this.appointmentDurationMinutes.hasOwnProperty(serviceKey);
  }

  getServiceDuration(serviceKey) {
    return this.appointmentDurationMinutes[serviceKey] || 30; // Default 30 minutes
  }

  canUseFeature(feature) {
    const featureMap = {
      whatsapp: ['basic', 'premium', 'business', 'elite'],
      voice: ['premium', 'business', 'elite'],
      marketing: ['business', 'elite'],
      payments: ['elite'],
      analytics: ['premium', 'business', 'elite'],
      multipleEmployees: ['business', 'elite']
    };

    return featureMap[feature]?.includes(this.tier) || false;
  }

  getMaxBookingsPerMonth() {
    const limits = {
      basic: 100,
      premium: 300,
      business: 500,
      elite: -1 // Unlimited
    };
    
    return limits[this.tier] || 100;
  }

  // Update Methods
  updateBasicInfo(updates) {
    const allowedFields = [
      'businessName',
      'businessType', 
      'formattedAddress',
      'numberOfChairs',
      'timezone'
    ];
    
    allowedFields.forEach(field => {
      if (updates.hasOwnProperty(field)) {
        this[field] = updates[field];
      }
    });

    this.updatedAt = new Date().toISOString();
    return this.validate();
  }

  updateWorkingHours(newHours) {
    this.workingHours = { ...this.workingHours, ...newHours };
    this.updatedAt = new Date().toISOString();
    return this.validateWorkingHours();
  }

  updateSettings(settingsUpdates) {
    this.settings = {
      ...this.settings,
      ...settingsUpdates,
      notifications: {
        ...this.settings.notifications,
        ...settingsUpdates.notifications
      },
      aiConfig: {
        ...this.settings.aiConfig,
        ...settingsUpdates.aiConfig
      },
      integrations: {
        ...this.settings.integrations,
        ...settingsUpdates.integrations
      }
    };

    this.updatedAt = new Date().toISOString();
    return { isValid: true, errors: [] };
  }

  updateTier(newTier, billingCycle = null) {
    const validTiers = ['basic', 'premium', 'business', 'elite'];
    
    if (!validTiers.includes(newTier)) {
      return {
        isValid: false,
        errors: ['Invalid subscription tier']
      };
    }

    this.tier = newTier;
    if (billingCycle) {
      this.billingCycle = billingCycle;
    }
    this.updatedAt = new Date().toISOString();
    
    return { isValid: true, errors: [] };
  }

  // Data Transformation Methods
  toFirebaseData() {
    return {
      // Existing Firebase structure (preserved)
      appointment_duration_minutes: this.appointmentDurationMinutes,
      formatted_address: this.formattedAddress,
      tier: this.tier,
      timezone: this.timezone,
      whatsapp_number: this.whatsappNumber,
      'working-hours': this.workingHours,
      number_of_chairs: this.numberOfChairs,
      business_name: this.businessName,
      business_type: this.businessType,
      owner_name: this.ownerName,
      owner_email: this.ownerEmail,
      billing_cycle: this.billingCycle,
      
      // New structure (additions)
      settings: this.settings,
      created_at: this.createdAt,
      updated_at: this.updatedAt,
      status: this.status
    };
  }

  toPublicProfile() {
    return {
      id: this.id,
      businessName: this.businessName,
      businessType: this.businessType,
      formattedAddress: this.formattedAddress,
      phoneNumber: this.phoneNumber,
      workingHours: this.workingHours,
      timezone: this.timezone,
      status: this.status
    };
  }

  toAnalyticsData() {
    return {
      businessType: this.businessType,
      tier: this.tier,
      numberOfChairs: this.numberOfChairs,
      servicesOffered: Object.keys(this.appointmentDurationMinutes).length,
      timezone: this.timezone,
      createdAt: this.createdAt
    };
  }

  // Static Factory Methods
  static fromRegistrationData(registrationData) {
    return new BusinessProfile({
      id: registrationData.phoneNumber,
      businessName: registrationData.businessName,
      businessType: registrationData.businessType,
      formattedAddress: registrationData.address,
      phoneNumber: registrationData.phoneNumber,
      whatsappNumber: `whatsapp:+${registrationData.phoneNumber.replace(/\D/g, '')}`,
      timezone: registrationData.timezone,
      numberOfChairs: registrationData.numberOfChairs,
      workingHours: registrationData.workingHours,
      appointmentDurationMinutes: Object.entries(registrationData.services).reduce((acc, [key, value]) => {
        acc[key] = parseInt(value.duration);
        return acc;
      }, {}),
      tier: registrationData.selectedPlan === 'plan1' ? 'basic' : 
            registrationData.selectedPlan === 'plan2' ? 'premium' : 
            registrationData.selectedPlan === 'plan3' ? 'business' : 'elite',
      billingCycle: registrationData.billingCycle,
      ownerName: registrationData.ownerName,
      ownerEmail: registrationData.ownerEmail,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active'
    });
  }

  static fromFirebaseData(id, firebaseData) {
    return new BusinessProfile({
      id,
      businessName: firebaseData.business_name,
      businessType: firebaseData.business_type,
      formattedAddress: firebaseData.formatted_address,
      phoneNumber: id, // Phone number is used as ID
      whatsappNumber: firebaseData.whatsapp_number,
      timezone: firebaseData.timezone,
      numberOfChairs: firebaseData.number_of_chairs,
      workingHours: firebaseData['working-hours'],
      appointmentDurationMinutes: firebaseData.appointment_duration_minutes,
      tier: firebaseData.tier,
      billingCycle: firebaseData.billing_cycle,
      ownerName: firebaseData.owner_name,
      ownerEmail: firebaseData.owner_email,
      settings: firebaseData.settings,
      createdAt: firebaseData.created_at,
      updatedAt: firebaseData.updated_at,
      status: firebaseData.status || 'active'
    });
  }
}